import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Clear as ClearIcon, Download as DownloadIcon } from '@mui/icons-material';
import { getReplenishmentOrders } from 'api/replenishment/get-replenishment-orders';
import { getReplenishmentOrdersCsv } from 'api/replenishment/get-replenishment-orders-csv';
import { GetReplenishmentOrdersVm } from 'api/replenishment/get-replenishment-orders.vm';
import { ReplenishmentOrderVm, OrderStatus } from 'api/replenishment/replenishment-order.vm';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'hooks/use-snackbar';
import { ProductVm } from 'api/catalog/product.vm';
import { LocationVm } from 'api/warehouse/location.vm';
import ProductSelectionDialog from 'components/ProductSelectionDialog';
import { LocationSelectionDialog } from 'components/LocationSelectionDialog';

const ReplenishmentOrdersPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [orders, setOrders] = useState<ReplenishmentOrderVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [warehouses, setWarehouses] = useState<GetWarehousesVm | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const warehouseId = searchParams.get('warehouseId') || '';
  const locationId = searchParams.get('locationId') || '';
  const productId = searchParams.get('productId') || '';
  const status = (searchParams.get('status') as OrderStatus | '') || '';

  const [selectedLocation, setSelectedLocation] = useState<LocationVm | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductVm | null>(null);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);

  useEffect(() => {
    getWarehouses('', {
      success: (vm: GetWarehousesVm) => {
        setWarehouses(vm);
        if (!warehouseId && vm.objects.length > 0) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('warehouseId', vm.objects[0].id);
          setSearchParams(newParams);
        }
      },
      error: () => {
        snackbar.show('Erro ao carregar armazéns', null, 'error');
      },
    });
  }, []);

  useEffect(() => {
    if (warehouseId) {
      fetchOrders(null, true);
    }
  }, [warehouseId, locationId, productId, status]);

  const fetchOrders = (offset: string | null, reset = false) => {
    if (!warehouseId) return;

    setLoading(true);
    getReplenishmentOrders(offset || '', warehouseId, locationId || undefined, productId || undefined, status || undefined, {
      success: (vm: GetReplenishmentOrdersVm) => {
        if (reset) {
          setOrders(vm.objects);
        } else {
          setOrders((prev) => [...prev, ...vm.objects]);
        }
        setNextOffset(vm.nextOffset);
        setHasMore(!!vm.nextOffset);
        setLoading(false);
      },
      error: () => {
        snackbar.show('Erro ao carregar ordens de reposição', null, 'error');
        setLoading(false);
      },
    });
  };

  const handleWarehouseChange = (newWarehouseId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('warehouseId', newWarehouseId);
    setSearchParams(newParams);
  };

  const handleStatusChange = (newStatus: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (newStatus) {
      newParams.set('status', newStatus);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams);
  };

  const handleLocationSelect = (location: LocationVm) => {
    setSelectedLocation(location);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('locationId', location.id);
    setSearchParams(newParams);
    setOpenLocationDialog(false);
  };

  const handleProductSelect = (product: ProductVm) => {
    setSelectedProduct(product);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('productId', product.id);
    setSearchParams(newParams);
    setOpenProductDialog(false);
  };

  const handleClearLocation = () => {
    setSelectedLocation(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('locationId');
    setSearchParams(newParams);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('productId');
    setSearchParams(newParams);
  };

  const handleDownloadCsv = () => {
    if (!warehouseId) return;

    setDownloadingCsv(true);
    getReplenishmentOrdersCsv(warehouseId, locationId || undefined, productId || undefined, status || undefined, {
      success: () => {
        snackbar.show('CSV transferido com sucesso', null, 'success');
        setDownloadingCsv(false);
      },
      error: () => {
        snackbar.show('Erro ao transferir CSV', null, 'error');
        setDownloadingCsv(false);
      },
    });
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      PENDING: 'Pendente',
      IN_PROGRESS: 'Em Progresso',
      FULFILLED: 'Concluída',
      CANCELED: 'Cancelada',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    const colorMap: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
      PENDING: 'warning',
      IN_PROGRESS: 'primary',
      FULFILLED: 'success',
      CANCELED: 'error',
    };
    return colorMap[status] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Ordens de Reposição
        </Typography>
        <Button
          variant="contained"
          startIcon={downloadingCsv ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={downloadingCsv || !warehouseId}
        >
          Exportar CSV
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Selecionar Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId || ''}
                label="Selecionar Armazém"
                onChange={(e) => handleWarehouseChange(e.target.value)}
              >
                {warehouses?.objects.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Estado</InputLabel>
              <Select
                labelId="status-select-label"
                value={status || ''}
                label="Estado"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={OrderStatus.PENDING}>Pendente</MenuItem>
                <MenuItem value={OrderStatus.IN_PROGRESS}>Em Progresso</MenuItem>
                <MenuItem value={OrderStatus.FULFILLED}>Concluída</MenuItem>
                <MenuItem value={OrderStatus.CANCELED}>Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Localização"
                value={selectedLocation?.id || ''}
                InputProps={{ readOnly: true }}
                onClick={() => setOpenLocationDialog(true)}
                sx={{ cursor: 'pointer' }}
              />
              {selectedLocation && (
                <IconButton onClick={handleClearLocation} size="small">
                  <ClearIcon />
                </IconButton>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label="Produto"
                value={selectedProduct?.name || ''}
                InputProps={{ readOnly: true }}
                onClick={() => setOpenProductDialog(true)}
                sx={{ cursor: 'pointer' }}
              />
              {selectedProduct && (
                <IconButton onClick={handleClearProduct} size="small">
                  <ClearIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Localização</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma ordem de reposição encontrada
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{order.locationId}</TableCell>
                  <TableCell>{order.productSku}</TableCell>
                  <TableCell>{order.productName}</TableCell>
                  <TableCell>{order.quantity}</TableCell>
                  <TableCell>
                    <Chip label={getStatusLabel(order.status)} color={getStatusColor(order.status)} size="small" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasMore && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button variant="outlined" onClick={() => fetchOrders(nextOffset)}>
            Carregar Mais
          </Button>
        </Box>
      )}

      {loading && orders.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <LocationSelectionDialog
        open={openLocationDialog}
        warehouseId={warehouseId}
        onClose={() => setOpenLocationDialog(false)}
        onLocationSelect={handleLocationSelect}
      />

      <ProductSelectionDialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} onProductSelect={handleProductSelect} />
    </Box>
  );
};

export default ReplenishmentOrdersPage;
